import {log} from '@/logger'
import {api} from '../../../services/api'
import {stashReturnPath} from '../../../navigation'

export type TokenOperationStatus = 'success' | 'login_required' | 'error'

export interface TokenResult {
  clientID: string
  personID: string
}

export interface TokenOperationResponse {
  status: TokenOperationStatus
  data?: TokenResult
  error?: Error
  // Only meaningful when `status === 'login_required'`, and consumed only inside
  // `exchangeWithRetry`. `false` marks a *terminal* re-auth cause — one that
  // retrying with a fresh access token can never fix (no refresh token exists,
  // or a bad client ID) — so the retry loop is skipped and we redirect
  // immediately. Omitted / `true` means the failure may be a transient
  // refresh-token rotation race worth retrying (AB#19499, AB#21765). Callers
  // outside TokenService don't need this: every `login_required` means the same
  // thing to them ("the user must re-authenticate").
  retriable?: boolean
}

export interface TokenServiceRetryConfig {
  // Total token-exchange attempts before a `login_required` result is treated
  // as terminal (the refresh token is genuinely dead and we must
  // re-authenticate).
  maxAttempts: number
  // Delay (ms) before each retry. Index 0 is the wait before attempt 2, index 1
  // before attempt 3, and so on; the last value is reused if there are more
  // retries than entries.
  backoffMs: number[]
}

// A single 403/`invalid_grant` is most often a refresh-token-rotation glitch: a
// concurrent caller rotated the refresh token, so this request used a stale one
// and got rejected even though the session is healthy. A fresh
// `getAccessTokenSilently` on retry usually clears it. Only sustained failures
// across all attempts mean the session is genuinely unrecoverable (AB#19499).
const DEFAULT_RETRY_CONFIG: TokenServiceRetryConfig = {
  maxAttempts: 5,
  // Five attempts with a compressed ramp. The rotation race almost always
  // clears on the first retry, so the later attempts are cheap insurance; the
  // ramp is kept short on purpose because a genuinely dead session pays the
  // full backoff before being redirected to login. This caps the worst-case
  // pre-redirect wait at ~3s (300+600+900+1200), down from ~7.5s under the old
  // 500→4000 exponential ramp (AB#19499).
  backoffMs: [300, 600, 900, 1200],
}

// A standalone 401/403 in the error message is a fallback signal for HTTP
// clients that don't surface a structured `status`. Word boundaries keep it
// from matching incidental digits — request IDs, durations like "4030ms" —
// which would otherwise misclassify an unrelated failure as login_required and
// send the user through a pointless retry-then-redirect (AB#19499).
const HTTP_AUTH_STATUS_IN_MESSAGE = /\b(401|403)\b/

export class TokenService {
  private readonly retryConfig: TokenServiceRetryConfig

  // Coalesces concurrent exchange callers onto a single in-flight operation.
  // With Auth0 refresh-token rotation, a second concurrent caller would use an
  // already-rotated (stale) refresh token and get a spurious 403; serializing
  // here removes that race at its source (AB#19499).
  private inFlight: Promise<TokenOperationResponse> | null = null

  // The clientID the in-flight exchange is running for. Coalescing only applies
  // to callers requesting the *same* client; a concurrent caller for a
  // different client must get its own exchange rather than silently receive the
  // in-flight client's tokens (AB#19499).
  private inFlightClientID: string | null = null

  constructor(
    private readonly getAccessTokenSilently: () => Promise<string>,
    private readonly loginWithRedirect: () => Promise<void>,
    retryConfig: Partial<TokenServiceRetryConfig> = {},
  ) {
    this.retryConfig = {...DEFAULT_RETRY_CONFIG, ...retryConfig}
  }

  private static toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(JSON.stringify(error))
  }

  private static delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms)
    })
  }

  /**
   * Exchange Auth0 access token for legacy JWT.
   * Core, single-attempt token exchange — no logging, retry, or redirect.
   * Maps the expired / invalid / rotated refresh-token failures (Auth0 error
   * codes, HTTP 401/403) to `login_required` so the caller can decide whether
   * to retry or re-authenticate.
   */
  async exchangeToken(clientID: string): Promise<TokenOperationResponse> {
    if (!clientID || clientID === '0') {
      // Terminal: the client ID won't change between retries, so retrying is
      // pointless. Marked non-retriable so `exchangeWithRetry` short-circuits
      // rather than looping 5× on the same bad input (AB#21765).
      const error = new Error('Invalid client ID for token exchange')
      return {status: 'login_required', retriable: false, error}
    }

    try {
      const accessToken = await this.getAccessTokenSilently()
      const response = await api.auth.exchange({accessToken, clientID})

      if (response.result === 'success' && response.data) {
        return {
          status: 'success',
          data: {
            clientID: response.data.clientID,
            personID: response.data.personID,
          },
        }
      }

      if (response.result === 'failure') {
        const error = new Error(response.error.message)
        return {status: 'error', error}
      }

      const error = new Error('Unexpected error during token exchange')
      return {status: 'error', error}
    } catch (err: unknown) {
      const error = TokenService.toError(err)
      // Handle Auth0-specific error cases
      const authError = err as {error?: string}
      if (authError?.error) {
        switch (authError.error) {
          case 'missing_refresh_token':
            // Auth0 throws this when there is no refresh token in localStorage
            // at all (incognito, cleared storage, fresh browser). Unlike
            // `invalid_grant` (a rotation race a fresh token clears on retry), a
            // refresh token can't materialize between synchronous retries — so
            // this is terminal: skip the loop and redirect now instead of paying
            // the full backoff on guaranteed-to-fail attempts (AB#21765).
            return {status: 'login_required', retriable: false, error}
          case 'login_required':
          case 'invalid_grant':
          case 'invalid_token':
          case 'consent_required':
          case 'interaction_required':
          case 'access_denied':
            return {status: 'login_required', error}
          default:
            // Continue to next error check
            break
        }
      }

      // Handle HTTP 401/403 errors (expired / forbidden refresh token).
      const status =
        err && typeof err === 'object' && 'status' in err
          ? (err as {status?: unknown}).status
          : undefined
      if (
        HTTP_AUTH_STATUS_IN_MESSAGE.test(error.message) ||
        status === 401 ||
        status === 403
      ) {
        return {status: 'login_required', error}
      }

      return {status: 'error', error}
    }
  }

  /**
   * Validate prerequisites and exchange the token, retrying transient
   * refresh-token failures before giving up. Concurrent callers for the same
   * client are coalesced onto one in-flight exchange so they can't race the
   * refresh-token rotation; a caller for a different client gets its own.
   */
  async validateAndExchangeToken(
    clientID: string | undefined,
    useIdpAuthentication: boolean,
  ): Promise<TokenOperationResponse> {
    if (!useIdpAuthentication) {
      const error = new Error('IDP authentication not enabled')
      return {status: 'error', error}
    }

    if (!clientID || clientID === '0') {
      // Terminal re-auth cause (see `exchangeToken`). This guard returns before
      // `exchangeWithRetry`, so no redirect fires here — the caller's dispatch
      // to `unauthenticated` drives the AuthRoute redirect (AB#21765). Marked
      // non-retriable to stay consistent with the `exchangeToken` guard.
      const error = new Error('Invalid client ID for token exchange')
      return {status: 'login_required', retriable: false, error}
    }

    if (this.inFlight && this.inFlightClientID === clientID) {
      return this.inFlight
    }

    const inFlight = this.exchangeWithRetry(clientID)
    this.inFlight = inFlight
    this.inFlightClientID = clientID
    try {
      return await inFlight
    } finally {
      // Only clear if a later caller hasn't already replaced the in-flight
      // exchange (e.g. a concurrent request for a different client).
      if (this.inFlight === inFlight) {
        this.inFlight = null
        this.inFlightClientID = null
      }
    }
  }

  /**
   * Attempt the exchange up to `maxAttempts` times. A single transient 403 /
   * `login_required` is retried (with a fresh access token each attempt) rather
   * than bouncing the user to login — that case usually recovers on its own.
   * Only once every attempt has failed with `login_required` do we treat the
   * session as unrecoverable and trigger re-authentication. A genuine `error`
   * (backend failure) is non-retriable and never redirects. A `login_required`
   * flagged `retriable: false` (no refresh token, bad client) is a *terminal*
   * re-auth cause — retrying can't help, so we skip the loop and redirect
   * immediately instead of paying the full backoff (AB#21765).
   */
  private async exchangeWithRetry(
    clientID: string,
  ): Promise<TokenOperationResponse> {
    const {maxAttempts, backoffMs} = this.retryConfig
    let lastResult: TokenOperationResponse = {
      status: 'error',
      error: new Error('Token exchange was not attempted'),
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const result = await this.exchangeToken(clientID)

      if (result.status === 'success') {
        if (attempt > 1) {
          log.breadcrumb(
            `Token exchange recovered on attempt ${attempt}/${maxAttempts}`,
          )
        }
        return result
      }

      // Only the expired/rotated refresh-token class is retriable; a genuine
      // error won't recover and must not trigger a re-auth redirect.
      if (result.status !== 'login_required') {
        return result
      }

      lastResult = result

      // Terminal re-auth cause (no refresh token, bad client): a fresh access
      // token can't fix it, so stop looping and fall through to the redirect
      // now rather than burning the full backoff on attempts that are
      // guaranteed to fail the same way (AB#21765).
      if (result.retriable === false) {
        log.breadcrumb(
          'Token exchange hit a terminal re-auth cause; skipping retries',
        )
        break
      }

      log.breadcrumb(
        `Token exchange attempt ${attempt}/${maxAttempts} hit login_required` +
          `${attempt < maxAttempts ? '; retrying' : '; retries exhausted'}`,
      )

      if (attempt < maxAttempts) {
        const wait =
          backoffMs[attempt - 1] ?? backoffMs[backoffMs.length - 1] ?? 0
        if (wait > 0) {
          // eslint-disable-next-line no-await-in-loop
          await TokenService.delay(wait)
        }
      }
    }

    // The refresh token is genuinely unrecoverable — either every attempt
    // failed with login_required (sustained 403s) or a terminal cause
    // short-circuited the loop above — so re-authenticate. This is the only path
    // that redirects; a transient glitch that recovered never reaches here.
    // Expired sessions are a normal lifecycle event, so this is not logged as an
    // error; only an unexpected redirect failure is.
    //
    // Remember where the user was before Auth0 takes the document away, so
    // CallbackView can put them back there instead of dropping them on /albums
    // (AB#25795). This must run BEFORE the await: the redirect navigates the
    // page, so anything after it may never execute. stashReturnPath validates
    // the value and no-ops on anything unsafe.
    stashReturnPath(`${window.location.pathname}${window.location.search}`)

    try {
      await this.loginWithRedirect()
    } catch (redirectErr) {
      const error = TokenService.toError(redirectErr)
      log.error(error)
      return {status: 'error', error}
    }

    return lastResult
  }
}
